import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db, functions, storage } from './firebase';
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref, getDownloadURL } from "firebase/storage";
import ActivityMap from './ActivityMap';
import { useWaterBasedStyles } from './hooks/useWaterBasedStyles';
import polyline from '@mapbox/polyline';
import './App.css';
import './HistoryPage.css';
import './SharePage.css';
import './LandingPage.css';
import './TargettedActivityGeneration.css';
import { usePostHog } from 'posthog-js/react';

function RequestBasedGenerationPage({ user }) {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const posthog = usePostHog();

    // State for the generation request data
    const [requestData, setRequestData] = useState(null);
    const [isLoadingRequest, setIsLoadingRequest] = useState(true);
    const [requestError, setRequestError] = useState(null);

    // State for templates and generation
    const [promptTemplates, setPromptTemplates] = useState([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [statusError, setStatusError] = useState('');

    // Pagination state for map styles
    const [currentStylePage, setCurrentStylePage] = useState(1);
    const itemsPerPage = 9;

    // Section visibility
    const [isStylesSectionExpanded, setIsStylesSectionExpanded] = useState(true);

    // Water analysis state
    const [waterAnalysis, setWaterAnalysis] = useState(null);
    const [waterThreshold, setWaterThreshold] = useState(5.0);
    const [isAdmin, setIsAdmin] = useState(false);

    // State for the activity-like object
    const [activityData, setActivityData] = useState(null);

    // Use the water-based styles hook for filtering
    const {
        defaultStyles: filteredTemplates,
        recommendedStyles,
        waterSummary,
        isWaterAnalyzed
    } = useWaterBasedStyles(promptTemplates, waterAnalysis, waterThreshold);

    // Load water threshold from settings and check admin status
    useEffect(() => {
        const loadWaterThreshold = async () => {
            try {
                const settingsDocRef = doc(db, 'settings', 'mapGeneration');
                const settingsDoc = await getDoc(settingsDocRef);
                if (settingsDoc.exists()) {
                    const settingsData = settingsDoc.data();
                    if (settingsData?.waterThreshold && typeof settingsData.waterThreshold === 'number') {
                        setWaterThreshold(settingsData.waterThreshold);
                    }
                }
            } catch (error) {
                console.error('Error loading water threshold:', error);
            }
        };

        const checkAdminStatus = async () => {
            if (user) {
                try {
                    const idTokenResult = await user.getIdTokenResult();
                    setIsAdmin(!!idTokenResult.claims.admin);
                } catch (error) {
                    console.error('Error checking admin status:', error);
                    setIsAdmin(false);
                }
            } else {
                setIsAdmin(false);
            }
        };

        loadWaterThreshold();
        checkAdminStatus();
    }, [user]);

    // Handler for water analysis results from ActivityMap
    const handleWaterAnalysis = (analysis) => {
        setWaterAnalysis(analysis);
        if (localStorage.getItem('waterDetectionDebug') === 'true') {
            console.log('RequestBasedGenerationPage: Water analysis received:', analysis);
        }
    };

    // Computed values for map styles pagination with default sort by date (newest first)
    const sortedStyles = [...filteredTemplates].sort((a, b) => {
        const dateA = a.createdAt?.toDate() || new Date(0);
        const dateB = b.createdAt?.toDate() || new Date(0);
        return dateB - dateA; // Newest first
    });

    const styleStartIndex = (currentStylePage - 1) * itemsPerPage;
    const styleEndIndex = styleStartIndex + itemsPerPage;
    const currentStyles = sortedStyles.slice(styleStartIndex, styleEndIndex);
    const totalStylePages = Math.ceil(sortedStyles.length / itemsPerPage);

    // Fetch prompt templates
    const fetchPromptTemplates = useCallback(async () => {
        if (!user) return;
        setIsLoadingTemplates(true);
        try {
            const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
            const result = await getTemplatesFunc();
            if (result.data && Array.isArray(result.data.templates)) {
                setPromptTemplates(result.data.templates);
            } else {
                setPromptTemplates([]);
            }
        } catch (error) {
            console.error("Error fetching prompt templates:", error);
            setPromptTemplates([]);
        } finally {
            setIsLoadingTemplates(false);
        }
    }, [user]);

    // Fetch generation request data
    const fetchGenerationRequest = useCallback(async () => {
        if (!requestId || !user) return;

        setIsLoadingRequest(true);
        setRequestError(null);

        try {
            const docRef = doc(db, "GenerationRequests", requestId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                console.log('Generation request data:', data);
                setRequestData(data);

                // Convert generation request data to activity-like format
                if (data.stravaStats) {
                    const activityLikeData = {
                        id: data.StravaId || requestId,
                        name: data.stravaStats.activityName || 'Untitled Activity',
                        type: data.stravaStats.activityType || 'Run',
                        distance: data.stravaStats.distance || 0,
                        moving_time: data.stravaStats.movingTime || 0,
                        total_elevation_gain: data.stravaStats.elevationGain || 0,
                        average_speed: data.stravaStats.averageSpeed || 0,
                        average_watts: data.stravaStats.averageWatts,
                        start_date: data.stravaStats.activityDate,
                        // We'll need to get the polyline from the stored GeoJSON or GPX
                        polyline: null
                    };

                    // Try to load polyline data from storage
                    if (data.geoJsonPath) {
                        console.log('Attempting to load GeoJSON from:', data.geoJsonPath);
                        try {
                            const geoJsonUrl = await getDownloadURL(ref(storage, data.geoJsonPath));
                            console.log('GeoJSON URL obtained:', geoJsonUrl);
                            const response = await fetch(geoJsonUrl);
                            const geoJson = await response.json();
                            console.log('GeoJSON loaded successfully');

                            // Extract coordinates from GeoJSON and convert to encoded polyline
                            // Handle both Feature and FeatureCollection formats
                            let coordinates = null;

                            if (geoJson.type === 'Feature' && geoJson.geometry) {
                                // Single Feature format
                                coordinates = geoJson.geometry.coordinates;
                            } else if (geoJson.features && geoJson.features[0] && geoJson.features[0].geometry) {
                                // FeatureCollection format
                                coordinates = geoJson.features[0].geometry.coordinates;
                            }

                            if (coordinates) {
                                console.log('Coordinates found, count:', coordinates.length);
                                // Convert coordinates to polyline format
                                // GeoJSON uses [lon, lat] format, we need to convert to [lat, lon]
                                const polylineCoords = coordinates.map(coord => [coord[1], coord[0]]);

                                // Encode the polyline
                                const encodedPolyline = polyline.encode(polylineCoords);
                                // ActivityMap expects the polyline in activity.map.summary_polyline
                                activityLikeData.map = {
                                    summary_polyline: encodedPolyline
                                };
                                console.log('Polyline encoded successfully');
                            } else {
                                console.error('Could not extract coordinates from GeoJSON:', geoJson);
                            }
                        } catch (error) {
                            console.error("Error loading GeoJSON:", error);
                            console.error("Full error details:", error.message, error.code);
                        }
                    } else {
                        console.log('No geoJsonPath found in generation request data');
                    }

                    setActivityData(activityLikeData);
                }
            } else {
                setRequestError("Generation request not found.");
            }
        } catch (error) {
            console.error("Error fetching generation request:", error);
            setRequestError("Failed to load generation request. Please try again.");
        } finally {
            setIsLoadingRequest(false);
        }
    }, [requestId, user]);

    // Initialize data on component mount
    useEffect(() => {
        // Reset water analysis when component mounts
        setWaterAnalysis(null);

        if (!user) {
            console.log('👤 No user logged in, redirecting to landing page');
            navigate('/');
            return;
        }

        // Check if user is admin
        user.getIdTokenResult().then((idTokenResult) => {
            if (!idTokenResult.claims.admin) {
                console.log('🚫 User is not admin, redirecting to landing page');
                navigate('/');
                return;
            }

            // Fetch data if user is admin
            fetchGenerationRequest();
            fetchPromptTemplates();
        }).catch((error) => {
            console.error('Error checking admin status:', error);
            navigate('/');
        });
    }, [user, fetchGenerationRequest, fetchPromptTemplates, navigate]);

    // Helper functions
    const formatDuration = (totalSeconds) => {
        if (isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        const pad = (num) => String(num).padStart(2, '0');

        if (hours > 0) {
            return `${hours}:${pad(minutes)}:${pad(seconds)}`;
        }
        return `${pad(minutes)}:${pad(seconds)}`;
    };

    const handleTemplateSelect = (template) => {
        posthog?.capture('prompt_template_selected', {
            template_id: template.id,
            template_name: template.name,
            page: 'request_based_generation',
            request_id: requestId
        });
        setSelectedTemplate(template);
        
        // Collapse styles section when template is selected (same as LandingPage)
        setIsStylesSectionExpanded(false);
        
        // Scroll to generation section after collapse animation
        setTimeout(() => {
            document.getElementById('generate-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
    };

    const handleGenerateMap = async () => {
        if (!selectedTemplate || !activityData || !requestData) {
            setStatusError('Please select a style for your map');
            return;
        }

        posthog?.capture('map_generation_started', {
            style: selectedTemplate.name || selectedTemplate.id,
            style_id: selectedTemplate.id,
            generation_type: 'request_based',
            request_id: requestId,
            original_activity_id: requestData.StravaId,
            timestamp: new Date().toISOString()
        });

        setIsGenerating(true);
        setStatusError('');

        try {
            // Create a new generation request that references the original activity
            // Don't include StravaId to avoid ownership validation
            const newRequestData = {
                // StravaId omitted to bypass ownership check
                stravaStats: requestData.stravaStats,
                promptTemplateId: selectedTemplate.id,
                status: 'pending',
                createdAt: serverTimestamp(),
                userId: user.uid,
                gpxDataSource: 'polyline',
                originalRequestId: requestId, // Reference to the original generation
                generatedByAdmin: true
            };

            // Only add adminGeneratedFrom if StravaId exists
            if (requestData.StravaId) {
                newRequestData.adminGeneratedFrom = requestData.StravaId;
            }

            // If we have GeoJSON data, include it
            if (requestData.geoJsonPath) {
                newRequestData.geoJsonPath = requestData.geoJsonPath;
            }
            
            // Don't copy originalImagePath - let the backend generate a new one

            const docRef = await addDoc(collection(db, 'GenerationRequests'), newRequestData);

            posthog?.capture('map_generation_created', {
                generation_id: docRef.id,
                template_id: selectedTemplate.id,
                page: 'request_based_generation',
                original_request_id: requestId
            });

            // Navigate to the share page for the new generation
            navigate(`/map/${docRef.id}`);
        } catch (error) {
            console.error("Error creating generation request:", error);
            setStatusError('Failed to start generation. Please try again.');
            setIsGenerating(false);

            posthog?.capture('map_generation_error', {
                error: error.message,
                page: 'request_based_generation',
                request_id: requestId
            });
        }
    };

    // Loading state
    if (isLoadingRequest || !isAdmin) {
        return (
            <div className="container">
                <div className="history-page">
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <div className="loading-spinner" style={{ margin: '0 auto 20px auto' }}></div>
                        <p>Loading generation data...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (requestError) {
        return (
            <div className="container">
                <div className="history-page">
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        background: 'var(--color-bg-subtle, #F9FAFB)',
                        border: '1px solid var(--color-border-card, #EAECF0)',
                        borderRadius: 'var(--border-radius-lg, 12px)'
                    }}>
                        <h2 style={{ color: 'var(--color-text-primary, #101828)', marginBottom: '15px' }}>Error</h2>
                        <p style={{ color: 'var(--color-text-secondary, #344054)', marginBottom: '20px' }}>{requestError}</p>
                        <button 
                            onClick={() => navigate('/')} 
                            className="submit-generation-button"
                            style={{ background: 'var(--Gray-600)', borderColor: 'var(--Gray-600)' }}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // No activity data
    if (!activityData || !requestData) {
        return (
            <div className="container">
                <div className="history-page">
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        background: 'var(--color-bg-subtle, #F9FAFB)',
                        border: '1px solid var(--color-border-card, #EAECF0)',
                        borderRadius: 'var(--border-radius-lg, 12px)'
                    }}>
                        <h2 style={{ color: 'var(--color-text-primary, #101828)', marginBottom: '15px' }}>No Activity Data</h2>
                        <p style={{ color: 'var(--color-text-secondary, #344054)', marginBottom: '20px' }}>This generation request doesn't contain activity data.</p>
                        <button 
                            onClick={() => navigate('/')} 
                            className="submit-generation-button"
                            style={{ background: 'var(--Gray-600)', borderColor: 'var(--Gray-600)' }}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="history-page" style={{ marginBottom: '40px' }}>
                <h2 style={{
                    fontSize: '30px',
                    fontWeight: '500',
                    lineHeight: '1.27',
                    color: 'var(--Gray-900, #101828)',
                    margin: '0 0 4px 0'
                }}>Generate New Map (Admin)</h2>
                <p className="history-subtitle">Creating a new map from request: {requestId}</p>
                <div className="history-divider"></div>

                                {/* Activity Card */}
                <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Activity Details</h3>
                <ul className="history-requests-list">
                    <li className="map-style-card">
                        <div className="history-thumbnail">
                            {activityData.map && activityData.map.summary_polyline ? (
                                <div className="activity-map-container">
                                    <ActivityMap 
                                        activity={activityData}
                                        width={320}
                                        height={200}
                                        interactive={false}
                                        onWaterAnalysis={handleWaterAnalysis}
                                    />
                                </div>
                            ) : (
                                <div className="map-placeholder" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0' }}>
                                    <p>Map data not available</p>
                                </div>
                            )}
                        </div>
                        
                        <div className="history-details">
                            <h3 className="activity-name">{activityData.name}</h3>
                            <div className="activity-metrics">
                                <div>
                                    <span>Distance</span>
                                    <strong>{(activityData.distance / 1000).toFixed(1)} km</strong>
                                </div>
                                <div>
                                    <span>Duration</span>
                                    <strong>{formatDuration(activityData.moving_time)}</strong>
                                </div>
                                <div>
                                    <span>Elevation</span>
                                    <strong>{Math.round(activityData.total_elevation_gain)} m</strong>
                                </div>
                            </div>
                            
                            {requestData.promptTemplateId && (
                                <p style={{ marginTop: '10px', fontSize: '14px', color: '#999' }}>
                                    Original template ID: {requestData.promptTemplateId}
                                </p>
                            )}
                        </div>
                    </li>
                </ul>
            </div>

                        {/* Map Styles Section */}
            <div className="history-page collapsible-section" style={{ marginBottom: '40px' }}>
                <div 
                    className="share-header-row collapsible-header"
                    style={{ marginBottom: '20px', cursor: 'pointer' }}
                    onClick={() => setIsStylesSectionExpanded(!isStylesSectionExpanded)}
                >
                    <div>
                        <h2 style={{
                            fontSize: '30px',
                            fontWeight: '500',
                            lineHeight: '1.27',
                            color: 'var(--Gray-900, #101828)',
                            margin: '0 0 4px 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            Choose a Map Style
                            <i className={`ti ${isStylesSectionExpanded ? 'ti-chevron-up' : 'ti-chevron-down'}`} 
                               style={{ fontSize: '24px', color: 'var(--Gray-500)' }}></i>
                        </h2>
                        <p className="history-subtitle">
                            {selectedTemplate ? 
                                `Selected: ${selectedTemplate.name}` : 
                                'Select a style for the new map generation'
                            }
                        </p>
                    </div>
                    {selectedTemplate && (
                        <div className="selection-status">
                            <i className="ti ti-circle-check" style={{ fontSize: '24px', color: 'var(--Primary-600)' }}></i>
                        </div>
                    )}
                </div>
                <div className="history-divider"></div>

                {/* Collapsed Style Preview */}
                {!isStylesSectionExpanded && selectedTemplate && (
                    <div className="collapsed-style-card">
                        <div className="style-thumbnail">
                            <img
                                src={selectedTemplate.exampleImageUrl || '/images/default-style.svg'}
                                alt={`${selectedTemplate.name} Style Map`}
                                onError={(e) => {
                                    e.target.src = '/images/default-style.svg';
                                }}
                            />
                        </div>
                        <div className="style-details">
                            <div className="style-title">
                                <h3>{selectedTemplate.name}</h3>
                            </div>
                            {selectedTemplate.description && (
                                <p className="style-description">{selectedTemplate.description}</p>
                            )}
                        </div>
                    </div>
                )}

                <div className={`collapsible-content ${isStylesSectionExpanded ? 'expanded' : 'collapsed'}`}>
                    {isLoadingTemplates ? (
                        <p style={{ textAlign: 'center' }}>Loading map styles...</p>
                    ) : filteredTemplates.length > 0 ? (
                        <>
                            {/* Water Analysis Summary (Admin Debug) */}
                            {isWaterAnalyzed && isAdmin && (
                                <div style={{
                                    background: 'var(--Primary-50, #FAFAF9)',
                                    border: '1px solid var(--Primary-200, #E7E5E4)',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '20px'
                                }}>
                                    <h4 style={{
                                        margin: '0 0 8px 0',
                                        color: 'var(--Primary-700, #44403C)',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <i className="ti ti-droplet" style={{ fontSize: '16px' }}></i>
                                        Water Content Analysis
                                    </h4>
                                    <p style={{
                                        margin: '0 0 8px 0',
                                        color: 'var(--Primary-600, #57534E)',
                                        fontSize: '13px'
                                    }}>
                                        {waterSummary.description}
                                    </p>
                                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                                        <span>Water: {waterSummary.percentage}%</span>
                                        <span>Recommended: {recommendedStyles.length} styles</span>
                                        <span>Showing: {filteredTemplates.length} styles</span>
                                    </div>
                                </div>
                            )}

                            <ul className="history-requests-list">
                                {/* Use the properly paginated and filtered currentStyles */}
                                {currentStyles.map((template, index) => {
                                    const templateId = template.id || `template-${index}`;
                                    const isRecommended = recommendedStyles.some(rec => rec.id === templateId);
                                    return (
                                        <li
                                            key={templateId}
                                            className={`map-style-card ${selectedTemplate?.id === templateId ? 'selected-template' : ''} ${isRecommended ? 'recommended-style' : ''}`}
                                            onClick={() => handleTemplateSelect({ ...template, id: templateId })}
                                            style={isRecommended ? {
                                                border: '2px solid var(--Primary-300, #D6D3D1)',
                                                boxShadow: '0px 4px 12px rgba(87, 83, 78, 0.15)'
                                            } : {}}
                                        >
                                            <div className="history-thumbnail">
                                                <img
                                                    src={template.exampleImageUrl || '/images/default-style.svg'}
                                                    alt={`${template.name} Style Map`}
                                                    onError={(e) => {
                                                        e.target.src = '/images/default-style.svg';
                                                    }}
                                                />
                                            </div>
                                            <div className="history-details" style={{ padding: '20px' }}>
                                                <h3 className="activity-name" style={{
                                                    margin: '0 0 8px 0',
                                                    fontSize: '18px',
                                                    fontWeight: '600'
                                                }}>{template.name}</h3>
                                                {template.description && (
                                                    <p style={{
                                                        margin: '0',
                                                        fontSize: '14px',
                                                        color: 'var(--Gray-500, #667085)',
                                                        lineHeight: '1.5'
                                                    }}>{template.description}</p>
                                                )}
                                            </div>
                                            {selectedTemplate?.id === templateId && (
                                                <div className="selection-indicator">
                                                    <i className="ti ti-circle-check" style={{ fontSize: '24px', color: 'var(--Primary-600)' }}></i>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>

                            {/* Only show pagination on desktop */}
                            {totalStylePages > 1 && (
                                <div className="pagination-controls">
                                    <button onClick={() => setCurrentStylePage(prev => Math.max(1, prev - 1))} disabled={currentStylePage === 1}>
                                        &larr; Previous
                                    </button>
                                    <span>Page {currentStylePage} of {totalStylePages}</span>
                                    <button onClick={() => setCurrentStylePage(prev => Math.min(totalStylePages, prev + 1))} disabled={currentStylePage === totalStylePages}>
                                        Next &rarr;
                                    </button>
                                </div>
                            )}
                        </>
                    ) : promptTemplates.length > 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            background: 'var(--Gray-50, #F9FAFB)',
                            borderRadius: '8px',
                            border: '1px solid var(--Gray-200, #EAECF0)'
                        }}>
                            <i className="ti ti-filter-off" style={{ fontSize: '48px', color: 'var(--Gray-400)', marginBottom: '16px' }}></i>
                            <h3 style={{ margin: '0 0 8px 0', color: 'var(--Gray-700)' }}>No suitable styles found</h3>
                            <p style={{ margin: '0', color: 'var(--Gray-500)', fontSize: '14px' }}>
                                Based on the water content analysis ({waterSummary.percentage}% water), 
                                no styles match this route type. Try selecting a different activity or check back later for more styles.
                            </p>
                            <button 
                                onClick={() => setWaterAnalysis(null)}
                                style={{
                                    marginTop: '16px',
                                    padding: '8px 16px',
                                    background: 'var(--Primary-600)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Show all styles
                            </button>
                        </div>
                    ) : (
                        <p style={{ textAlign: 'center' }}>No map styles available.</p>
                    )}
                </div>
            </div>

            {/* Generate Button Section */}
            <div id="generate-section" className="history-page" style={{ marginBottom: '40px' }}>
                <h2 style={{
                    fontSize: '30px',
                    fontWeight: '500',
                    lineHeight: '1.27',
                    color: 'var(--Gray-900, #101828)',
                    margin: '0 0 4px 0'
                }}>Generate the Map</h2>
                <p className="history-subtitle">Create a new map with the selected style</p>
                <div className="history-divider"></div>

                <div className="generation-section">
                    {/* Selected Template Summary */}
                    {selectedTemplate && (
                        <div className="selected-summary">
                            <h3>Selected Style</h3>
                            <div className="summary-card">
                                <span>{selectedTemplate.name}</span>
                                {selectedTemplate.description && (
                                    <span className="summary-description">{selectedTemplate.description}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Generation Button */}
                    <div className="generation-controls">
                        <button
                            onClick={handleGenerateMap}
                            disabled={!selectedTemplate || isGenerating}
                            className="submit-generation-button"
                        >
                            {isGenerating ? 'Generating Your Mythic Map...' : 
                             !selectedTemplate ? 'Please select a style' : 
                             'Generate New Map'}
                        </button>

                        {/* Status Messages */}
                        {!selectedTemplate ? (
                            <p className="generation-hint">
                                Please select a style to generate the new map
                            </p>
                        ) : (
                            <div style={{
                                marginTop: '16px',
                                padding: '12px',
                                backgroundColor: '#F0FDF4',
                                border: '1px solid #BBF7D0',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <p style={{
                                    color: '#16A34A',
                                    fontSize: '14px',
                                    margin: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}>
                                    <i className="ti ti-check-circle" style={{ fontSize: '16px' }}></i>
                                    Ready to generate!
                                </p>
                            </div>
                        )}

                        {/* Error Messages */}
                        {statusError && (
                            <p className="error" style={{ marginTop: '20px', textAlign: 'center' }}>
                                {statusError}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RequestBasedGenerationPage;